import type {
  ImportSession,
  ChunkUploadResponse,
  DatasetWithPreview,
  FileFormat,
  ExistingDatasetConflict,
  ImportError,
} from '../types/dataset';
import type { ColumnTypeAssignment, InferredColumn, ColumnType } from '../types/columnType';

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
const ROUTE_PREFIX = '/chart-studio';

function getApiUrl(): string {
  if (import.meta.env.VITE_ENVIRONMENT === 'dev') {
    try {
      const stored = localStorage.getItem('vibe-dev-mode-config');
      if (stored) {
        const devConfig = JSON.parse(stored);
        if (devConfig.apiTarget === 'local') {
          return 'http://localhost:8000';
        }
      }
    } catch {
      // Ignore localStorage errors
    }
  }
  return import.meta.env.VITE_API_URL || 'http://localhost:8000';
}

function getToken(): string | null {
  const key = Object.keys(sessionStorage).find((k) =>
    k.startsWith('oidc.user:')
  );
  if (!key) return null;
  try {
    const data = JSON.parse(sessionStorage.getItem(key) || '');
    return data?.access_token || null;
  } catch {
    return null;
  }
}

function buildHeaders(contentType?: string): Record<string, string> {
  const headers: Record<string, string> = {};
  if (contentType) {
    headers['Content-Type'] = contentType;
  }
  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

const CONTENT_TYPE_MAP: Record<FileFormat, string> = {
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  json: 'application/json',
  parquet: 'application/vnd.apache.parquet',
};

export function detectFileFormat(filename: string): FileFormat | null {
  const ext = filename.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'csv':
      return 'csv';
    case 'xlsx':
      return 'xlsx';
    case 'json':
      return 'json';
    case 'parquet':
    case 'pq':
      return 'parquet';
    default:
      return null;
  }
}

export type StartImportResult =
  | { type: 'success'; session: ImportSession }
  | { type: 'conflict'; conflict: ExistingDatasetConflict }
  | { type: 'error'; error: ImportError };

export async function startImport(
  workspaceId: string,
  filename: string,
  fileSizeBytes: number,
  format: FileFormat
): Promise<StartImportResult> {
  const url = `${getApiUrl()}${ROUTE_PREFIX}/import/start`;
  const response = await fetch(url, {
    method: 'POST',
    headers: buildHeaders('application/json'),
    body: JSON.stringify({
      workspace_id: workspaceId,
      filename,
      file_size_bytes: fileSizeBytes,
      content_type: CONTENT_TYPE_MAP[format],
    }),
  });

  if (response.status === 201) {
    const session: ImportSession = await response.json();
    return { type: 'success', session };
  }

  if (response.status === 409) {
    const body = await response.json();
    // FastAPI wraps HTTPException in { detail: {...} }
    const detail = body.detail || body;
    // Distinguish between "active session" error and "existing dataset" conflict
    if (detail.existing_dataset) {
      return { type: 'conflict', conflict: detail as ExistingDatasetConflict };
    }
    // Active session conflict — treat as error
    return {
      type: 'error',
      error: {
        error_type: detail.error_type || 'parse_failure',
        message: detail.message || 'A conflict occurred.',
        guidance: detail.guidance || 'Please try again.',
      },
    };
  }

  const body = await response.json();
  const detail = body.detail || body;
  const error: ImportError = {
    error_type: detail.error_type || 'parse_failure',
    message: detail.message || 'An error occurred.',
    guidance: detail.guidance || 'Please try again.',
  };
  return { type: 'error', error };
}

export async function uploadChunks(
  sessionId: string,
  file: File,
  onProgress: (percent: number) => void,
  abortSignal?: AbortSignal
): Promise<void> {
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

  for (let i = 0; i < totalChunks; i++) {
    if (abortSignal?.aborted) {
      throw new Error('Upload cancelled');
    }

    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);

    const formData = new FormData();
    formData.append('chunk', chunk);
    formData.append('chunk_index', String(i));

    const url = `${getApiUrl()}${ROUTE_PREFIX}/import/${sessionId}/chunk`;
    const response = await fetch(url, {
      method: 'POST',
      headers: buildHeaders(),
      body: formData,
      signal: abortSignal,
    });

    if (!response.ok) {
      const error: ImportError = await response.json();
      throw error;
    }

    const result: ChunkUploadResponse = await response.json();
    onProgress(result.progress_percent);
  }
}

export async function cancelImport(sessionId: string): Promise<void> {
  const url = `${getApiUrl()}${ROUTE_PREFIX}/import/${sessionId}/cancel`;
  await fetch(url, {
    method: 'POST',
    headers: buildHeaders('application/json'),
  });
}

export async function getImportStatus(
  sessionId: string
): Promise<ImportSession> {
  const url = `${getApiUrl()}${ROUTE_PREFIX}/import/${sessionId}/status`;
  const response = await fetch(url, {
    headers: buildHeaders('application/json'),
  });

  if (!response.ok) {
    throw new Error('Failed to fetch import status');
  }

  return response.json();
}

export async function confirmReplace(sessionId: string): Promise<ImportSession> {
  const url = `${getApiUrl()}${ROUTE_PREFIX}/import/${sessionId}/confirm-replace`;
  const response = await fetch(url, {
    method: 'POST',
    headers: buildHeaders('application/json'),
  });

  if (!response.ok) {
    throw new Error('Failed to confirm replacement');
  }

  return response.json();
}

export async function getDataset(
  workspaceId: string
): Promise<DatasetWithPreview | null> {
  const url = `${getApiUrl()}${ROUTE_PREFIX}/datasets/${workspaceId}`;
  const response = await fetch(url, {
    headers: buildHeaders('application/json'),
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error('Failed to fetch dataset');
  }

  return response.json();
}

export async function pollUntilComplete(
  sessionId: string,
  onProgress: (percent: number) => void,
  abortSignal?: AbortSignal,
  intervalMs = 1000
): Promise<ImportSession> {
  while (true) {
    if (abortSignal?.aborted) {
      throw new Error('Polling cancelled');
    }

    const session = await getImportStatus(sessionId);
    onProgress(session.progress_percent);

    if (session.status === 'complete' || session.status === 'failed' || session.status === 'cancelled') {
      return session;
    }

    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
}

export async function clearActiveImport(workspaceId: string): Promise<void> {
  const url = `${getApiUrl()}${ROUTE_PREFIX}/import/clear/${workspaceId}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: buildHeaders('application/json'),
  });

  if (!response.ok) {
    throw new Error('Failed to clear active import');
  }
}

export const MAX_FILE_SIZE = 1073741824; // 1GB
export const SUPPORTED_EXTENSIONS = ['csv', 'xlsx', 'json', 'parquet', 'pq'];

// --- Column Type System API ---

export async function inferTypesServerSide(
  datasetId: string,
  columns: { column_name: string; column_index: number; values: (string | null)[] }[]
): Promise<InferredColumn[]> {
  const url = `${getApiUrl()}${ROUTE_PREFIX}/columns/infer-types`;
  const response = await fetch(url, {
    method: 'POST',
    headers: buildHeaders('application/json'),
    body: JSON.stringify({ dataset_id: datasetId, columns }),
  });

  if (!response.ok) {
    throw new Error('Failed to infer column types');
  }

  const data = await response.json();
  return data.columns;
}

export async function getColumnTypes(
  datasetId: string
): Promise<ColumnTypeAssignment[] | null> {
  const url = `${getApiUrl()}${ROUTE_PREFIX}/datasets/${datasetId}/columns/types`;
  const response = await fetch(url, {
    headers: buildHeaders('application/json'),
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error('Failed to fetch column types');
  }

  const data = await response.json();
  return data.columns;
}

export async function saveColumnTypes(
  datasetId: string,
  columns: { column_index: number; column_type: ColumnType }[]
): Promise<ColumnTypeAssignment[]> {
  const url = `${getApiUrl()}${ROUTE_PREFIX}/datasets/${datasetId}/columns/types`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: buildHeaders('application/json'),
    body: JSON.stringify({ columns }),
  });

  if (!response.ok) {
    throw new Error('Failed to save column types');
  }

  const data = await response.json();
  return data.columns;
}
